"use client";

import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle2, Loader2, Circle } from "lucide-react";

export default function Preview() {
  const [passedCount, setPassedCount] = useState(0);
  const totalTests = 4;

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;

    const runCycle = () => {
      setPassedCount((prev) => {
        // Wenn alle Tests + Wartezeit durch sind, starte von vorne
        if (prev >= totalTests + 1) {
          return 0;
        }
        return prev + 1;
      });
      // Nach dem Erreichen von totalTests etwas länger warten
      timeoutId = setTimeout(runCycle, passedCount >= totalTests ? 2000 : 500);
    };

    timeoutId = setTimeout(runCycle, 500);

    return () => clearTimeout(timeoutId);
  }, [passedCount]);

  // Wir nutzen prev = totalTests + 1 als Pausen-Zustand, deckeln aber die Anzeige auf totalTests.
  const displayCount = Math.min(passedCount, totalTests);
  const isComplete = displayCount === totalTests;

  return (
    <div className="relative flex h-full w-full flex-col items-center justify-center overflow-hidden rounded-xl p-6">
      <div className="bg-grid-slate-200/50 dark:bg-grid-zinc-800/10 absolute inset-0 -z-10 [mask-image:linear-gradient(0deg,white,rgba(255,255,255,0.6))] dark:[mask-image:linear-gradient(0deg,rgba(0,0,0,1),rgba(0,0,0,0.6))]" />

      <div className="flex flex-col items-center gap-4">
        {/* Test Icons */}
        <div className="flex items-center gap-2">
          {Array.from({ length: totalTests }).map((_, i) => {
            const isPassed = displayCount > i;
            const isCurrent = displayCount === i && !isComplete;

            return (
              <div
                key={i}
                className="relative flex h-6 w-6 items-center justify-center"
              >
                <AnimatePresence mode="popLayout">
                  {isPassed ? (
                    <motion.div
                      key="passed"
                      initial={{ scale: 0.5, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      exit={{ scale: 0.5, opacity: 0 }}
                      transition={{
                        type: "spring",
                        stiffness: 300,
                        damping: 20,
                      }}
                    >
                      <CheckCircle2 className="h-5 w-5 text-emerald-500" />
                    </motion.div>
                  ) : isCurrent ? (
                    <motion.div
                      key="running"
                      initial={{ scale: 0.5, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      exit={{ scale: 0.5, opacity: 0 }}
                    >
                      <Loader2 className="h-5 w-5 animate-spin text-blue-500" />
                    </motion.div>
                  ) : (
                    <motion.div
                      key="pending"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                    >
                      <Circle className="h-5 w-5 text-zinc-300 dark:text-zinc-600" />
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </div>

        {/* Progress Bar */}
        <div className="h-1.5 w-32 overflow-hidden rounded-full bg-zinc-200 dark:bg-zinc-700">
          <motion.div
            className={`h-full rounded-full ${isComplete ? "bg-emerald-500" : "bg-blue-500"}`}
            initial={{ width: "0%" }}
            animate={{ width: `${(displayCount / totalTests) * 100}%` }}
            transition={{ duration: 0.4, ease: "easeOut" }}
          />
        </div>

        {/* Status Text */}
        <motion.div className="relative mt-1 flex h-4 w-32 justify-center overflow-hidden">
          <AnimatePresence mode="popLayout">
            <motion.p
              key={isComplete ? "pass" : "run"}
              className={`absolute font-mono text-xs font-semibold ${
                isComplete
                  ? "text-emerald-600 dark:text-emerald-400"
                  : "text-blue-600 dark:text-blue-400"
              }`}
              initial={{ y: 15, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: -15, opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              {isComplete ? "PASS" : "RUNNING"} {displayCount}/{totalTests}
            </motion.p>
          </AnimatePresence>
        </motion.div>
      </div>
    </div>
  );
}
